import RequiredMark from './ui/RequiredMark';
import { useMemo, useState } from 'react';
import { KeyRound, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { isSsoOnlyAccount } from '../lib/auth/providerUtils';
import { linkPasswordToAccount, validatePasswordStrength } from '../lib/auth/accountManagement';
import { friendly } from '../lib/errors';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';

function dismissKey(uid: string) {
  return `arevina:backup-password-dismissed:v1:${uid}`;
}

function isDismissed(uid: string): boolean {
  try {
    return localStorage.getItem(dismissKey(uid)) === '1';
  } catch {
    return false;
  }
}

function markDismissed(uid: string) {
  try {
    localStorage.setItem(dismissKey(uid), '1');
  } catch {
    // ignore
  }
}

/**
 * One-time soft prompt for existing Google-only accounts to add an email/password
 * backup login. Reuses linkPasswordToAccount (same path as Account Settings).
 * Mount only in authenticated app shells (not signup), so registration isn't interrupted.
 */
export default function BackupPasswordPrompt() {
  const { user, profile } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ssoOnly = useMemo(() => isSsoOnlyAccount(user), [user]);

  if (!user || !profile || !ssoOnly || dismissed) return null;
  if (isDismissed(user.uid)) return null;

  const handleDismiss = () => {
    markDismissed(user.uid);
    setDismissed(true);
    setOpen(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    const strengthError = validatePasswordStrength(password);
    if (strengthError) {
      setError(strengthError);
      return;
    }
    setSaving(true);
    try {
      await linkPasswordToAccount(user, password);
      markDismissed(user.uid);
      setDismissed(true);
      setOpen(false);
    } catch (err) {
      setError(friendly(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className="border-b border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-800/50 dark:bg-emerald-950/40 dark:text-emerald-100"
        role="status"
        data-testid="backup-password-prompt"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-4 py-2.5 sm:px-6">
          <div className="flex min-w-0 items-start gap-2 text-sm">
            <KeyRound className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <p>
              <span className="font-medium">Add a backup password</span>
              {' - '}
              you signed in with Google. Setting a password lets you still reach Arevina if you lose
              Google access. Optional, recommended.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="border-emerald-300 bg-[var(--surface-card)]/80 text-emerald-950 hover:bg-emerald-100 dark:border-emerald-700 dark:bg-transparent dark:text-emerald-100 dark:hover:bg-emerald-900/50 min-h-11"
              onClick={() => setOpen(true)}
            >
              Set password
            </Button>
            <button
              type="button"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-emerald-800 hover:bg-emerald-100 dark:text-emerald-200 dark:hover:bg-emerald-900/50"
              aria-label="Dismiss"
              onClick={handleDismiss}
            >
              <X className="h-5 w-5" aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <form onSubmit={(e) => void handleSave(e)}>
            <DialogHeader>
              <DialogTitle>Set a backup password</DialogTitle>
              <DialogDescription>
                Link email/password sign-in to this Google account. You can skip this and set it later
                in Settings.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="backup-password">
                  <RequiredMark />
                  New password
                </Label>
                <Input
                  id="backup-password"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <p className="text-xs text-[var(--text-muted)]">At least 8 characters</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="backup-confirm">
                  <RequiredMark />
                  Confirm password
                </Label>
                <Input
                  id="backup-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>
              {error ? (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                  {error}
                </p>
              ) : null}
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button type="button" variant="ghost" onClick={handleDismiss}>
                Not now
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : 'Save password'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
